/**
 * 런타임 에러 → 텔레메트리 `error` 이벤트 속성 변환 (순수 함수만).
 *
 * 임패커스 OS는 error 이벤트를 낱개로 쌓지 않고 지문(에러 타입 + 정규화 메시지 +
 * 최상위 스택 프레임 + 라우트 패턴)으로 묶어 발생 횟수만 올린다 (error_group).
 * 지문에 쓰이는 속성 키가 정해져 있으므로 이름을 바꾸면 안 된다:
 *   errorKind(타입) / errorLabel(메시지) / stackTop(정규화 스택) / sourceRef / handled
 *
 * 스택·메시지 정리 규칙은 OS 쪽 정규화(impakers-os `src/lib/error-triage/fingerprint.ts`)와
 * 같게 맞췄다. 어긋나면 같은 버그가 배포마다 다른 그룹으로 쪼개진다.
 */

const MAX_LABEL_LENGTH = 500
const MAX_STACK_FRAMES = 3

/**
 * 텔레메트리 sanitizer는 값 **어딘가에** 이메일 패턴이 있으면 문자열 전체를
 * `[redacted]` 로 바꾼다. 그러면 서버는 메시지 대신 페이지 경로로 그룹을 만들어
 * 그 페이지의 모든 에러가 한 덩어리가 된다. 보내기 전에 해당 조각만 치환한다.
 */
const EMAIL_LIKE = /[^\s@/]+@[^\s@/]+\.[^\s@/]+/g
/** 010-1234-5678 · 01012345678 · 02-123-4567 · +82 10 ... — 학부모/강사 번호가 메시지에 섞이는 경우 */
const PHONE_LIKE = /(?:\+?82[-\s]?)?\b0\d{1,2}[-\s.]?\d{3,4}[-\s.]?\d{4}\b/g

export interface CapturedTelemetryError {
  /** `TypeError`, `PostgrestError` 등. 모르면 null (서버가 메시지에서 다시 뽑는다). */
  errorKind: string | null
  /** 사람이 읽는 메시지. 이메일·전화번호는 치환된 상태. */
  errorLabel: string
  /** 정규화된 상위 스택 프레임 (`a < b < c`). 없으면 빈 문자열. */
  stackTop: string
  /** PostgrestError.code(`42501` 등) 같은 에러 코드. */
  errorCode: string | null
  /** 원본 스택에 브라우저 확장 프레임이 있는지 — 우리 코드 에러가 아니다. */
  fromBrowserExtension: boolean
}

/**
 * 스택에서 배포마다 달라지는 부분을 걷어내고 상위 프레임만 남긴다.
 *
 * pnpm/turbopack 경로의 `pkg@1.2.3` 조각은 이메일 패턴에 걸려 스택 전체가
 * `[redacted]` 로 날아가므로 버전 스펙 제거가 특히 중요하다.
 */
export function normalizeStack(rawStack: string | null | undefined, maxFrames = MAX_STACK_FRAMES): string {
  const stack = (rawStack ?? '').trim()
  if (!stack) return ''

  return stack
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('at ') || /\(.*:\d+:\d+\)/.test(line) || /@.*:\d+:\d+/.test(line))
    .slice(0, maxFrames)
    .map(normalizeStackFrame)
    .filter(Boolean)
    .join(' < ')
}

export function normalizeStackFrame(rawFrame: string): string {
  const frame = rawFrame
    .trim()
    .replace(/^at\s+/, '')
    .replace(/webpack-internal:\/{2,3}/g, '')
    .replace(/webpack:\/\/[^/]*\//g, '')
    .replace(/rsc:\/{2,3}/g, '')
    // 오리진 (배포·도메인마다 다름)
    .replace(/https?:\/\/[^/\s)]+/gi, '')
    .replace(/\?[^\s)]*/g, '')
    // 패키지 버전 스펙 — sanitizer 오탐의 주범
    .replace(/([A-Za-z0-9_+.-]+)@\d+\.\d+\.\d+[A-Za-z0-9_.+-]*/g, '$1')
    // 빌드 해시가 박힌 청크 파일명: page-4f2a9c1b.js → page.js
    .replace(/-[0-9a-f]{8,}(?=\.[a-z]+\b)/gi, '')
    .replace(/\s+/g, ' ')
    .trim()

  return frame.replace(EMAIL_LIKE, '<pkg>')
}

/** 메시지에 섞인 개인정보성 조각을 치환하고 길이를 자른다. */
export function scrubErrorLabel(raw: string): string {
  return raw
    .replace(EMAIL_LIKE, '<email>')
    .replace(PHONE_LIKE, '<phone>')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_LABEL_LENGTH)
}

function extractErrorType(message: string): string | null {
  const match = /^([A-Z][A-Za-z0-9_]*(?:Error|Exception))\b/.exec(message.trim())
  return match ? match[1] : null
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key]
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function stringifyUnknown(value: unknown): string {
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    return String(value)
  }
}

/**
 * 던져진 값(Error·PostgrestError·문자열·임의 객체)을 전송용 형태로 바꾼다.
 * 메시지가 비면 보낼 가치가 없으므로 null.
 */
export function toCapturedError(value: unknown): CapturedTelemetryError | null {
  let kind: string | null = null
  let label = ''
  let rawStack: string | null = null
  let code: string | null = null

  if (typeof value === 'string') {
    label = value
    kind = extractErrorType(value)
  } else if (value !== null && typeof value === 'object') {
    // Error 인스턴스와 supabase `{ message, code, details }` 객체를 같은 경로로 읽는다.
    const record = value as Record<string, unknown>
    const name = readString(record, 'name')
    label = readString(record, 'message') ?? name ?? stringifyUnknown(value)
    kind = (name && name !== 'Error' ? name : null) ?? extractErrorType(label) ?? name
    rawStack = readString(record, 'stack')
    const rawCode = record.code
    code = typeof rawCode === 'string' || typeof rawCode === 'number' ? String(rawCode) : null
  } else if (value !== undefined && value !== null) {
    label = String(value)
  }

  const errorLabel = scrubErrorLabel(label)
  if (!errorLabel) return null

  return {
    errorKind: kind,
    errorLabel,
    stackTop: normalizeStack(rawStack),
    errorCode: code,
    fromBrowserExtension: /(?:chrome|moz|safari(?:-web)?)-extension:\/\//.test(rawStack ?? ''),
  }
}

/**
 * 보내도 원인을 알 수 없거나, 우리 코드와 무관하거나, 정상 흐름인 에러.
 */
export function isIgnorableTelemetryError(captured: CapturedTelemetryError): boolean {
  if (captured.fromBrowserExtension) return true
  // 사용자가 페이지를 떠나거나 요청을 취소한 것 — 에러가 아니다.
  if (captured.errorKind === 'AbortError' || captured.errorKind === 'CancelledError') return true
  const label = captured.errorLabel
  // 브라우저가 레이아웃 계산을 다음 프레임으로 미뤘다는 경고. 동작 영향 없음.
  if (/^ResizeObserver loop/i.test(label)) return true
  // CORS 없는 외부 스크립트(메타 픽셀 등)의 에러는 내용이 가려진 채로 온다.
  if (/^Script error\.?$/i.test(label)) return true
  return false
}

/**
 * 같은 에러의 폭주를 막는 스로틀. 에러 루프가 OS 엔드포인트를 두드리지 않게
 * 같은 에러는 간격을 두고, 페이지 수명 동안 총 전송 수에도 상한을 둔다.
 */
export class TelemetryErrorThrottle {
  private readonly lastSentAt = new Map<string, number>()
  private sentCount = 0

  constructor(
    private readonly intervalMs = 60_000,
    private readonly limit = 20,
  ) {}

  shouldSend(captured: CapturedTelemetryError, now = Date.now()): boolean {
    if (this.sentCount >= this.limit) return false

    const key = `${captured.errorKind ?? ''}|${captured.errorLabel.slice(0, 120)}|${captured.stackTop.slice(0, 120)}`
    const previous = this.lastSentAt.get(key)
    if (previous !== undefined && now - previous < this.intervalMs) return false

    this.lastSentAt.set(key, now)
    this.sentCount += 1
    return true
  }
}
